import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Search, Save, X } from 'lucide-react';
import { GlassCard } from '@/components/design/GlassCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { AdminAPI } from '@/services/api';

interface Category {
  id: string;
  name: string;
  slug: string;
  _count?: { posts: number };
}

const CategoryPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await AdminAPI.categories.getAll();
      setCategories(data);
    } catch (error) {
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await AdminAPI.categories.create({ name: newCategoryName });
      toast.success('Category created');
      setNewCategoryName('');
      setIsCreating(false);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const handleUpdate = async (id: string) => {
    if (!editName.trim()) return;
    try {
      await AdminAPI.categories.update(id, { name: editName });
      toast.success('Category updated');
      setEditingId(null);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure? This will affect posts in this category.')) return;
    try {
      await AdminAPI.categories.delete(id);
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const filteredCategories = categories.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600">
            Categories
          </h1>
          <p className="text-white/60 mt-1">Manage blog post categories</p>
        </div>
        <Button onClick={() => setIsCreating(true)} className="bg-blue-600 text-white hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Category
        </Button>
      </div>

      {isCreating && (
        <GlassCard className="p-6">
          <div className="flex items-center gap-4">
            <Input
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Enter category name..."
              className="max-w-md bg-slate-950 border-white/10 text-white placeholder:text-gray-400"
              autoFocus
            />
            <Button onClick={handleCreate} className="bg-green-500 hover:bg-green-600">
              <Save className="w-4 h-4 mr-2" /> Save
            </Button>
            <Button onClick={() => setIsCreating(false)} variant="ghost" className="hover:bg-red-500/20 hover:text-red-400">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </GlassCard>
      )}

      <div className="flex items-center gap-4 bg-slate-900/50 p-4 rounded-xl border border-white/5">
        <Search className="w-5 h-5 text-white/40" />
        <input
          type="text"
          placeholder="Search categories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="bg-transparent border-none focus:outline-none text-white w-full placeholder:text-white/30"
        />
      </div>

      <div className="grid gap-4">
        {filteredCategories.map((category) => (
          <GlassCard key={category.id} className="p-4 flex items-center justify-between group hover:border-white/20 transition-all">
            {editingId === category.id ? (
              <div className="flex items-center gap-4 w-full">
                 <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="max-w-md bg-navy-800/50 border-white/10"
                  />
                  <Button onClick={() => handleUpdate(category.id)} size="sm" className="bg-green-500/20 text-green-400 hover:bg-green-500/30">
                    <Save className="w-4 h-4" />
                  </Button>
                  <Button onClick={() => setEditingId(null)} size="sm" variant="ghost">
                    <X className="w-4 h-4" />
                  </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 font-bold">
                    {category.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{category.name}</h3>
                    <p className="text-sm text-white/40">/{category.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="hover:bg-white/10"
                    onClick={() => {
                      setEditingId(category.id);
                      setEditName(category.name);
                    }}
                  >
                    <Edit2 className="w-4 h-4 text-white/60" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="hover:bg-red-500/20 hover:text-red-400"
                    onClick={() => handleDelete(category.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </>
            )}
          </GlassCard>
        ))}

        {!loading && filteredCategories.length === 0 && (
          <div className="text-center py-12 text-white/40">
            No categories found.
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
